import {Alert} from 'antd';
import React, {useCallback, useRef, useState} from 'react';
import './PollingScrollGuard.less';

export interface ScrollAwarePollingState {
    isPollingPaused: boolean,
    pollInterval: number,
    scrollToTopRequest: number,
    onScrollTopChange: (isAtTop: boolean) => boolean,
    requestScrollToTop: () => boolean
}

export function useScrollAwarePolling(pollInterval?: number): ScrollAwarePollingState {
    const configuredPollInterval = pollInterval && pollInterval > 0 ? pollInterval : 0;
    const isAtTopRef = useRef(true);
    const [isAtTop, setIsAtTop] = useState(true);
    const [scrollToTopRequest, setScrollToTopRequest] = useState(0);

    const onScrollTopChange = useCallback((nextIsAtTop: boolean) => {
        const resumed = configuredPollInterval > 0 && !isAtTopRef.current && nextIsAtTop;
        isAtTopRef.current = nextIsAtTop;
        setIsAtTop(nextIsAtTop);
        return resumed;
    }, [configuredPollInterval]);

    const requestScrollToTop = useCallback(() => {
        if (configuredPollInterval <= 0 || isAtTopRef.current){
            return false;
        }
        setScrollToTopRequest(request => request + 1);
        return true;
    }, [configuredPollInterval]);

    const isPollingPaused = configuredPollInterval > 0 && !isAtTop;
    return {
        isPollingPaused,
        pollInterval: isPollingPaused ? 0 : configuredPollInterval,
        scrollToTopRequest,
        onScrollTopChange,
        requestScrollToTop
    };
}

interface PollingPausedAlertProps {
    visible: boolean,
    onClick: () => void
}

export const PollingPausedAlert: React.FC<PollingPausedAlertProps> = ({visible, onClick}) => {
    if (!visible){
        return null;
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' '){
            event.preventDefault();
            onClick();
        }
    }

    return <div
        className="polling-paused-alert"
        role="button"
        tabIndex={0}
        aria-label="Return to the top and resume automatic updates"
        onClick={onClick}
        onKeyDown={handleKeyDown}
    >
        <Alert
            type="info"
            showIcon
            message={<>
                <span>Updates paused</span>
                <span className="polling-paused-alert-action">Back to top</span>
            </>}
        />
    </div>
}
